/**
 * Allowlist sanitiser for floor-plan SVGs that are mounted as LIVE DOM.
 *
 * Why this exists: the viewer parses the stored plan with DOMParser and hands the
 * resulting element to `L.svgOverlay`, which inserts it into the app document.
 * `<script>` elements from a DOMParser document do not run there, but event-handler
 * attributes and animation elements do, and an inline `<style>` applies to the whole
 * page. Plans are user uploads (the Locations dialog posts the file verbatim and the
 * API accepts any media object), so the tree has to be reduced to what the viewer
 * contract needs before it is inserted.
 *
 * Allowlist, not denylist: anything not named here is removed together with its
 * subtree. That is deliberately lossy for exotic content (SVG fonts, animation,
 * foreignObject, hyperlinks) — none of which a floor plan needs, and every one of
 * which is a way to run or fetch something.
 *
 * What the viewer contract needs and this keeps:
 *   - `#Room` and every shape's `data-code` / `data-name` (zone clicks and pins),
 *   - `data-buildlog-editor` on the root (editor round-trip),
 *   - `<image>` backgrounds, but only with `data:image/...` hrefs (real plans embed
 *     their raster; an external href would be a fetch from the viewer's origin),
 *   - `<style>` from CAD/Illustrator exports, rebuilt rule by rule, scoped to the plan
 *     root, with every URL-capable value dropped except same-document `url(#id)`.
 *
 * Same-document references (`<use>`, `<textPath>`, gradient/pattern/filter `href`,
 * `url(#id)` in paint, clip, mask, marker and filter values, `#id` selectors in the
 * stylesheet, ARIA IDREF lists such as `aria-labelledby`) are the one kind of reference
 * kept, and only in the `#id` form. Once the plan is live DOM such a reference resolves
 * against the whole app document, so every `id` in the plan is namespaced with the
 * plan's scope token and every reference is rewritten the same way: a reference can
 * then only ever land inside this plan, and a plan's `id`s cannot shadow an app
 * element's. The exception is `#Room`, which is the id the viewer and the editor read
 * the plan BY (`PLAN_ROOM_ID`). An `href` or IDREF whose target does not exist in the
 * plan is dropped.
 *
 * Fixed point: sanitising already-sanitised output changes nothing. `getSvgText`
 * stores sanitised text in the persisted cache and the viewer sanitises again at
 * render, so the scope token is reused from the root when it is already there (and
 * well-formed), ids already carrying it are left alone, and a stylesheet selector
 * already prefixed with the scope is not prefixed twice.
 */

import { safeRandomUUID } from '@/lib/helpers/safeRandomUUID.ts'

const SVG_NS = 'http://www.w3.org/2000/svg'
const XLINK_NS = 'http://www.w3.org/1999/xlink'
const XML_NS = 'http://www.w3.org/XML/1998/namespace'
const XMLNS_NS = 'http://www.w3.org/2000/xmlns/'

/** Attribute written on the plan root so a plan's own stylesheet cannot reach the app. */
export const PLAN_SCOPE_ATTR = 'data-plan-scope'

/**
 * The id a plan is read by — `svgDeserializer`, `prepareSvgPlan` and the pin tests look
 * the room group up as `#Room` — so it is the one id never namespaced. Kept equal to
 * `ROOM_GROUP_ID` in `mapEditor/lib/svgSerializer.ts` (pinned by the sanitiser test);
 * not imported from there because this module must not pull the editor in.
 */
export const PLAN_ROOM_ID = 'Room'

/**
 * The scope token: `p` plus 12 hex digits, random so two plans persisted from
 * different sessions never share one, and shaped so that a value found on an upload
 * can be checked before it is written into a selector and every id.
 */
const SCOPE_VALUE = /^p[0-9a-f]{12}$/
/** A scope token at the head of an id, from an earlier sanitisation of the same plan. */
const SCOPE_ID_PREFIX = /^p[0-9a-f]{12}-/

function newScopeValue(): string {
  return `p${safeRandomUUID().replace(/-/g, '').slice(0, 12)}`
}

type IdMapper = (id: string) => string

/**
 * The namespaced form of an id. A stale scope prefix (the plan was sanitised under
 * another token, e.g. re-saved by the editor, which writes a fresh root) is replaced,
 * not stacked, so ids do not grow by one prefix per round-trip.
 */
function namespaceId(id: string, scope: string): string {
  if (id === '' || id === PLAN_ROOM_ID) return id
  return `${scope}-${id.replace(SCOPE_ID_PREFIX, '')}`
}

const identityId: IdMapper = id => id

const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'defs',
  'symbol',
  'use',
  'switch',
  'title',
  'desc',
  'style',
  'image',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'path',
  'text',
  'tspan',
  'textPath',
  'linearGradient',
  'radialGradient',
  'stop',
  'pattern',
  'clipPath',
  'mask',
  'marker',
  'filter',
  // Filter primitives that only transform pixels already in the plan. feImage is left
  // out on purpose: it is the one primitive that loads a resource by href.
  'feBlend',
  'feColorMatrix',
  'feComponentTransfer',
  'feComposite',
  'feDisplacementMap',
  'feDropShadow',
  'feFlood',
  'feFuncA',
  'feFuncB',
  'feFuncG',
  'feFuncR',
  'feGaussianBlur',
  'feMerge',
  'feMergeNode',
  'feMorphology',
  'feOffset',
  'feTurbulence',
])

const ALLOWED_ATTRIBUTES = new Set([
  // identity / structure
  'id',
  'class',
  'style',
  'version',
  'baseProfile',
  'viewBox',
  'preserveAspectRatio',
  'transform',
  'href',
  // geometry
  'x',
  'y',
  'width',
  'height',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x1',
  'y1',
  'x2',
  'y2',
  'fx',
  'fy',
  'fr',
  'points',
  'd',
  'pathLength',
  // paint
  'fill',
  'fill-opacity',
  'fill-rule',
  'stroke',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'opacity',
  'color',
  'clip-path',
  'clip-rule',
  'mask',
  'filter',
  'display',
  'visibility',
  'overflow',
  'isolation',
  'mix-blend-mode',
  'vector-effect',
  'shape-rendering',
  'image-rendering',
  'color-interpolation',
  'color-interpolation-filters',
  'paint-order',
  'marker-start',
  'marker-mid',
  'marker-end',
  // text
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'font-variant',
  'text-anchor',
  'text-decoration',
  'dominant-baseline',
  'alignment-baseline',
  'baseline-shift',
  'letter-spacing',
  'word-spacing',
  'writing-mode',
  'dx',
  'dy',
  'rotate',
  'textLength',
  'lengthAdjust',
  'startOffset',
  // gradients, patterns, clip, mask, marker
  'gradientUnits',
  'gradientTransform',
  'spreadMethod',
  'offset',
  'stop-color',
  'stop-opacity',
  'patternUnits',
  'patternContentUnits',
  'patternTransform',
  'clipPathUnits',
  'maskUnits',
  'maskContentUnits',
  'markerWidth',
  'markerHeight',
  'markerUnits',
  'refX',
  'refY',
  'orient',
  // conditional processing inside <switch>
  'requiredExtensions',
  'requiredFeatures',
  'systemLanguage',
  // filters
  'filterUnits',
  'primitiveUnits',
  'in',
  'in2',
  'result',
  'stdDeviation',
  'mode',
  'type',
  'values',
  'operator',
  'k1',
  'k2',
  'k3',
  'k4',
  'flood-color',
  'flood-opacity',
  'radius',
  'tableValues',
  'slope',
  'intercept',
  'amplitude',
  'exponent',
  'baseFrequency',
  'numOctaves',
  'seed',
  'stitchTiles',
  'scale',
  'xChannelSelector',
  'yChannelSelector',
])

/**
 * ARIA attributes whose value is an IDREF or a space-separated IDREF list (WAI-ARIA
 * 1.2 §6.2). Live in the app document they resolve like any other reference, so each
 * token goes through the id mapper and a token whose target did not survive is
 * dropped. Every other `aria-*` value is text and is carried verbatim.
 */
const ARIA_IDREF_ATTRIBUTES = new Set([
  'aria-activedescendant',
  'aria-controls',
  'aria-describedby',
  'aria-details',
  'aria-errormessage',
  'aria-flowto',
  'aria-labelledby',
  'aria-owns',
])

/** Raster (and inert-in-image-context SVG) data URLs — the only non-fragment href kept. */
const DATA_IMAGE_HREF = /^data:image\/(?:png|jpe?g|gif|webp|bmp|avif|svg\+xml)[;,]/i

/** CSS functions that can make the browser fetch something. */
const URL_CAPABLE_FUNCTION =
  /(?:^|[^\w-])(?:-webkit-)?(?:url|image-set|image|element|cross-fade|src)\s*\(/i

/**
 * The one URL form a plan legitimately uses: a same-document reference, `url(#id)`.
 * An escaped id (`url(#\31 23)`) never reaches this pattern: a declaration's value
 * comes out of the CSSOM with url escapes already decoded (`url("#123")`), and a
 * presentation attribute carrying a backslash is dropped by `hasExternalReference`.
 */
const FRAGMENT_URL = /url\(\s*(['"]?)#([^'"()\s\\]*)\1\s*\)/gi

const HEX_DIGIT = /^[0-9a-f]$/i
const CSS_WHITESPACE = /^[ \t\n\r\f]$/
const CSS_NEWLINE = /^[\n\r\f]$/

/** A name code point (CSS Syntax §4.2): letter, digit, `-`, `_` or non-ASCII. */
function isCssNameCodePoint(ch: string): boolean {
  return /^[-\w]$/.test(ch) || ch.charCodeAt(0) >= 0x80
}

/**
 * "Consume an escaped code point" (CSS Syntax §4.3.7); `start` is the index after the
 * backslash. A hex escape takes up to six digits and swallows one following
 * whitespace (CRLF counting as one); zero, a surrogate and an out-of-range value
 * decode to U+FFFD, as does a backslash at the end of input.
 */
function consumeCssEscape(text: string, start: number): { value: string; end: number } {
  if (start >= text.length) return { value: '\uFFFD', end: start }
  let i = start
  if (HEX_DIGIT.test(text[i])) {
    let hex = ''
    while (i < text.length && hex.length < 6 && HEX_DIGIT.test(text[i])) hex += text[i++]
    if (text[i] === '\r' && text[i + 1] === '\n') i += 2
    else if (i < text.length && CSS_WHITESPACE.test(text[i])) i++
    const codePoint = parseInt(hex, 16)
    const value =
      codePoint === 0 || (codePoint >= 0xd800 && codePoint <= 0xdfff) || codePoint > 0x10ffff
        ? '\uFFFD'
        : String.fromCodePoint(codePoint)
    return { value, end: i }
  }
  const value = String.fromCodePoint(text.codePointAt(i)!)
  return { value, end: i + value.length }
}

/**
 * "Consume a name" (CSS Syntax §4.3.11) starting at `start`: name code points and
 * escapes, returned decoded. Null when no name starts there.
 */
function consumeCssName(text: string, start: number): { name: string; end: number } | null {
  let name = ''
  let i = start
  while (i < text.length) {
    const ch = text[i]
    if (isCssNameCodePoint(ch)) {
      name += ch
      i++
    } else if (ch === '\\' && !CSS_NEWLINE.test(text[i + 1] ?? '')) {
      const escaped = consumeCssEscape(text, i + 1)
      name += escaped.value
      i = escaped.end
    } else {
      break
    }
  }
  return name ? { name, end: i } : null
}

/**
 * "Serialize an identifier" (CSSOM §2.1) — byte for byte what `CSS.escape` returns.
 * Implemented here rather than called because the test runtime (jsdom 29) has no
 * `CSS` namespace, and because the fixed point needs the same spelling on every
 * platform, not whatever the host's serializer happens to produce.
 */
export function serializeCssIdentifier(value: string): string {
  let out = ''
  const codePoints = Array.from(value)
  for (let i = 0; i < codePoints.length; i++) {
    const ch = codePoints[i]
    const cp = ch.codePointAt(0)!
    const isDigit = cp >= 0x30 && cp <= 0x39
    const isLetter = (cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a)
    if (cp === 0) out += '\uFFFD'
    else if ((cp >= 0x1 && cp <= 0x1f) || cp === 0x7f) out += `\\${cp.toString(16)} `
    else if (isDigit && (i === 0 || (i === 1 && codePoints[0] === '-'))) {
      out += `\\${cp.toString(16)} `
    } else if (i === 0 && ch === '-' && codePoints.length === 1) out += '\\-'
    else if (cp >= 0x80 || ch === '-' || ch === '_' || isDigit || isLetter) out += ch
    else out += `\\${ch}`
  }
  return out
}

/**
 * The href policy. `<image>` loads bytes, so it gets a raster data URL and nothing
 * else — a fragment on it is not a reference, it is the document's own URL, which
 * no plan needs. Everything else (`use`, `textPath`, gradients, patterns, filters)
 * may only point at a same-document fragment; whether that fragment exists in the
 * plan is checked once the tree is reduced, in `sanitizeSvgPlanTree`.
 */
function isSafeHref(elementName: string, value: string): boolean {
  const v = value.trim()
  if (elementName === 'image') return DATA_IMAGE_HREF.test(v)
  return v.length > 1 && v.startsWith('#')
}

/** Every `url(#id)` in a CSS value, with the id mapped through `mapId`. */
export function namespaceFragmentUrls(value: string, mapId: IdMapper): string {
  return value.replace(
    FRAGMENT_URL,
    (_match, quote: string, id: string) => `url(${quote}#${mapId(id)}${quote})`,
  )
}

/**
 * True when `value` can make the browser fetch anything other than `url(#id)`.
 *
 * Any backslash counts: CSS decodes escapes inside identifiers, so `u\72l(` IS `url(`
 * to the browser while the pattern above never sees that spelling. Decoding escapes
 * here would mean re-implementing the CSS tokenizer; plans do not need escapes.
 */
export function hasExternalReference(value: string): boolean {
  if (value.includes('\\')) return true
  return URL_CAPABLE_FUNCTION.test(value.replace(FRAGMENT_URL, ''))
}

function isAllowedAttribute(attr: Attr): boolean {
  if (attr.namespaceURI === XMLNS_NS) return true
  if (attr.namespaceURI === XML_NS) return attr.localName === 'space' || attr.localName === 'lang'
  if (attr.namespaceURI === XLINK_NS) return attr.localName === 'href'
  if (attr.namespaceURI !== null) return false
  const name = attr.localName
  if (name.startsWith('data-') || name.startsWith('aria-')) return true
  return ALLOWED_ATTRIBUTES.has(name)
}

let scratchDocument: Document | null = null

/**
 * A document with no browsing context: style parsed here is never applied to the app
 * and nothing it references is loaded.
 */
function getScratchDocument(): Document {
  if (!scratchDocument) scratchDocument = document.implementation.createHTMLDocument('')
  return scratchDocument
}

function safeDeclarations(style: CSSStyleDeclaration, mapId: IdMapper): string {
  const out: string[] = []
  for (let i = 0; i < style.length; i++) {
    const name = style.item(i)
    const value = style.getPropertyValue(name)
    if (!value || hasExternalReference(value)) continue
    const priority = style.getPropertyPriority(name)
    out.push(`${name}: ${namespaceFragmentUrls(value, mapId)}${priority ? ` !${priority}` : ''}`)
  }
  return out.join('; ')
}

/** Inline `style="..."`, reduced to declarations that cannot fetch. */
export function sanitizeInlineStyle(cssText: string, mapId: IdMapper = identityId): string {
  const probe = getScratchDocument().createElement('div')
  probe.setAttribute('style', cssText)
  return safeDeclarations(probe.style, mapId)
}

/**
 * Split a selector list on top-level commas (not inside `()`, `[]` or quotes, and not
 * an escaped comma, which is part of a name: `#a\,b`).
 */
function splitSelectorList(selectorText: string): string[] {
  const parts: string[] = []
  let depth = 0
  let quote: string | null = null
  let start = 0
  for (let i = 0; i < selectorText.length; i++) {
    const ch = selectorText[i]
    if (ch === '\\') {
      i++
      continue
    }
    if (quote) {
      if (ch === quote) quote = null
      continue
    }
    if (ch === '"' || ch === "'") quote = ch
    else if (ch === '(' || ch === '[') depth++
    else if (ch === ')' || ch === ']') depth--
    else if (ch === ',' && depth === 0) {
      parts.push(selectorText.slice(start, i))
      start = i + 1
    }
  }
  parts.push(selectorText.slice(start))
  return parts.map(p => p.trim()).filter(Boolean)
}

/** A scope prefix an earlier sanitisation put on this selector, whatever its token. */
const SCOPE_SELECTOR_PREFIX = new RegExp(`^\\[${PLAN_SCOPE_ATTR}="[^"]*"\\]\\s+`)

/**
 * `#id` selectors mapped through `mapId`, so a stylesheet keeps addressing the
 * elements it addressed before their ids were namespaced. Only outside quotes and
 * `[...]` — inside those a `#` is text, not an id selector.
 *
 * The name after `#` is tokenized the way the browser tokenizes it, escapes included:
 * `#\31 23` IS `#123` (the only way to write a numeric id, which CAD exports produce),
 * so the id is mapped in its decoded form and written back serialized. Matching the
 * raw spelling instead would rename the element and leave the rule pointing at the
 * old name, silently un-styling it.
 */
function namespaceIdSelectors(selector: string, mapId: IdMapper): string {
  let out = ''
  let quote: string | null = null
  let bracketDepth = 0
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i]
    if (ch === '\\') {
      // An escape anywhere else is copied as written: in `.a\#b` the `#` is part of
      // a class name, not an id selector.
      out += ch
      if (i + 1 < selector.length) out += selector[++i]
      continue
    }
    if (quote) {
      out += ch
      if (ch === quote) quote = null
      continue
    }
    if (ch === '"' || ch === "'") quote = ch
    else if (ch === '[') bracketDepth++
    else if (ch === ']') bracketDepth = Math.max(0, bracketDepth - 1)
    else if (ch === '#' && bracketDepth === 0) {
      const name = consumeCssName(selector, i + 1)
      if (name) {
        out += `#${serializeCssIdentifier(mapId(name.name))}`
        i = name.end - 1
        continue
      }
    }
    out += ch
  }
  return out
}

const CSS_STYLE_RULE = 1
const CSS_MEDIA_RULE = 4

function rebuildRules(rules: CSSRuleList, scope: string, mapId: IdMapper): string {
  let out = ''
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]
    if (rule.type === CSS_STYLE_RULE) {
      const styleRule = rule as CSSStyleRule
      const declarations = safeDeclarations(styleRule.style, mapId)
      if (!declarations) continue
      const selectors = splitSelectorList(styleRule.selectorText).map(
        s => `${scope} ${namespaceIdSelectors(s.replace(SCOPE_SELECTOR_PREFIX, ''), mapId)}`,
      )
      if (selectors.length === 0) continue
      out += `${selectors.join(', ')} { ${declarations} }\n`
    } else if (rule.type === CSS_MEDIA_RULE) {
      const mediaRule = rule as CSSMediaRule
      const inner = rebuildRules(mediaRule.cssRules, scope, mapId)
      if (inner) out += `@media ${mediaRule.media.mediaText} { ${inner} }\n`
    }
    // Every other rule kind (@import, @font-face, @namespace, @keyframes, @supports,
    // @layer, ...) is dropped: none is needed to colour a plan, and several fetch.
  }
  return out
}

/**
 * A plan's `<style>` text, rebuilt from the browser's own parse: only style and
 * media rules survive, every selector is prefixed with `scope` (a prefix left by an
 * earlier pass is replaced, not stacked), `#id` selectors and `url(#id)` values go
 * through `mapId`, and every URL-capable value is dropped.
 */
export function sanitizeStylesheet(
  cssText: string,
  scope: string,
  mapId: IdMapper = identityId,
): string {
  // A constructed sheet belongs to no document, so nothing in it is applied or
  // fetched (browsers even ignore @import in replaceSync). Every browser at the app's
  // floor has it; the inert-document path below is only for a runtime without it.
  if (typeof CSSStyleSheet === 'function' && 'replaceSync' in CSSStyleSheet.prototype) {
    try {
      const sheet = new CSSStyleSheet()
      sheet.replaceSync(cssText)
      return rebuildRules(sheet.cssRules, scope, mapId)
    } catch {
      // fall through to the inert document
    }
  }
  const doc = getScratchDocument()
  const styleElement = doc.createElement('style')
  styleElement.textContent = cssText
  doc.head.appendChild(styleElement)
  try {
    const sheet = styleElement.sheet
    return sheet ? rebuildRules(sheet.cssRules, scope, mapId) : ''
  } finally {
    styleElement.remove()
  }
}

export class SvgPlanSanitizeError extends Error {}

function isHrefAttribute(attr: Attr): boolean {
  return attr.localName === 'href' && (attr.namespaceURI === null || attr.namespaceURI === XLINK_NS)
}

/**
 * Reduce a parsed plan to the allowlist, in place. `root` must be the `<svg>` element
 * of a DOMParser document that has NOT been inserted into the page yet.
 */
export function sanitizeSvgPlanTree(root: Element): void {
  if (root.namespaceURI !== SVG_NS || root.localName !== 'svg') {
    throw new SvgPlanSanitizeError('Plan root is not an <svg> element')
  }

  // Reusing the token is what makes a second pass a fixed point; the shape check is
  // what keeps an upload from choosing a token that would break out of the selector.
  const existingScope = root.getAttribute(PLAN_SCOPE_ATTR)
  const scopeValue =
    existingScope !== null && SCOPE_VALUE.test(existingScope) ? existingScope : newScopeValue()
  const scopeSelector = `[${PLAN_SCOPE_ATTR}="${scopeValue}"]`
  const mapId: IdMapper = id => namespaceId(id, scopeValue)

  const kept: Element[] = []
  const stack: Element[] = [root]
  while (stack.length > 0) {
    const element = stack.pop()!
    kept.push(element)

    for (const attr of Array.from(element.attributes)) {
      if (!isAllowedAttribute(attr)) {
        element.removeAttributeNode(attr)
        continue
      }
      const isHref = attr.localName === 'href' && attr.namespaceURI !== XMLNS_NS
      if (isHref) {
        if (!isSafeHref(element.localName, attr.value)) element.removeAttributeNode(attr)
        continue
      }
      if (attr.localName === 'style' && attr.namespaceURI === null) {
        const cleaned = sanitizeInlineStyle(attr.value, mapId)
        if (cleaned) attr.value = cleaned
        else element.removeAttributeNode(attr)
        continue
      }
      if (attr.namespaceURI === XMLNS_NS) continue
      if (hasExternalReference(attr.value)) {
        element.removeAttributeNode(attr)
        continue
      }
      // Paint, clip, mask, marker and filter references. `data-*` is the consumers'
      // own field and is carried verbatim; `aria-*` never holds url(), and its IDREF
      // lists are resolved below, once the set of surviving ids is known.
      const name = attr.localName
      if (!name.startsWith('data-') && !name.startsWith('aria-') && /url\(/i.test(attr.value)) {
        attr.value = namespaceFragmentUrls(attr.value, mapId)
      }
    }

    if (element.localName === 'style') {
      // Text only: the rebuilt sheet replaces whatever children (text, CDATA) it had.
      element.textContent = sanitizeStylesheet(element.textContent ?? '', scopeSelector, mapId)
      continue
    }

    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element
        if (childElement.namespaceURI === SVG_NS && ALLOWED_ELEMENTS.has(childElement.localName)) {
          stack.push(childElement)
        } else {
          element.removeChild(child)
        }
      } else if (child.nodeType !== Node.TEXT_NODE && child.nodeType !== Node.CDATA_SECTION_NODE) {
        // Comments and processing instructions carry nothing the viewer reads.
        element.removeChild(child)
      }
    }
  }

  // Ids first, over the reduced tree only, so the set of targets is complete and
  // holds nothing a removed subtree used to define; then every fragment href and
  // every ARIA IDREF is mapped the same way and must land on one of them.
  const ids = new Set<string>()
  for (const element of kept) {
    const id = element.getAttribute('id')
    if (id === null) continue
    const mapped = mapId(id)
    if (mapped !== id) element.setAttribute('id', mapped)
    ids.add(mapped)
  }
  for (const element of kept) {
    for (const attr of Array.from(element.attributes)) {
      if (isHrefAttribute(attr) && element.localName !== 'image') {
        const target = mapId(attr.value.trim().slice(1))
        if (ids.has(target)) attr.value = `#${target}`
        else element.removeAttributeNode(attr)
      } else if (attr.namespaceURI === null && ARIA_IDREF_ATTRIBUTES.has(attr.localName)) {
        const targets = attr.value
          .split(/[ \t\n\r\f]+/)
          .filter(Boolean)
          .map(mapId)
          .filter(target => ids.has(target))
        if (targets.length > 0) attr.value = targets.join(' ')
        else element.removeAttributeNode(attr)
      }
    }
  }

  root.setAttribute(PLAN_SCOPE_ATTR, scopeValue)
}

/** Parse, sanitise and serialise — for consumers that need the plan as text. */
export function sanitizeSvgPlanText(svgText: string): string {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  sanitizeSvgPlanTree(doc.documentElement)
  return new XMLSerializer().serializeToString(doc.documentElement)
}
